import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { PullToRefresh } from '@/components/PullToRefresh';
import { FileText, Search, ExternalLink, Sparkles, Loader2, ChevronRight, RefreshCw, Cloud, CloudOff, CheckCircle2, AlertCircle, Bell, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['CAO', 'CAR', 'CAAP', 'MOS', 'AIP', 'ERSA', 'ERSA_GEN'];

const statusColors = {
  current: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  review_due: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  superseded: 'bg-red-500/10 text-red-400 border-red-500/20',
};

export default function CASALibraryPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('ALL');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [question, setQuestion] = useState('');
  
  // Auto-sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [showUpdates, setShowUpdates] = useState(false);

  const fetchDocuments = useCallback(async (category) => {
    try {
      const url = category && category !== 'ALL'
        ? `${API}/casa/documents?category=${category}`
        : `${API}/casa/documents`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setDocuments(await res.json());
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/casa/auto-fetch/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSyncStatus(data.sync_status);
        setRecentUpdates(data.recent_updates || []);
      }
    } catch (err) {
      console.error('Sync status error:', err);
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/casa/auto-fetch/sync`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchDocuments(activeTab);
        await fetchSyncStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Sync failed');
      }
    } catch (err) {
      toast.error('Sync request failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAutoSync = async (enabled) => {
    try {
      const res = await fetch(`${API}/casa/auto-fetch/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ auto_sync_enabled: enabled })
      });
      if (res.ok) {
        setSyncStatus(prev => ({ ...prev, auto_sync_enabled: enabled }));
        toast.success(enabled ? 'Auto-sync enabled' : 'Auto-sync disabled');
      }
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const handleDismissUpdate = async (updateId) => {
    try {
      await fetch(`${API}/casa/updates/${updateId}/dismiss`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setRecentUpdates(prev => prev.filter(u => u.update_id !== updateId));
    } catch (err) {
      console.error('Dismiss error:', err);
    }
  };

  const handleRefresh = useCallback(async () => {
    await fetchDocuments(activeTab);
    await fetchSyncStatus();
    toast.success('Documents refreshed');
  }, [fetchDocuments, fetchSyncStatus, activeTab]);

  useEffect(() => {
    fetchDocuments(activeTab);
    fetchSyncStatus();
  }, [activeTab, fetchDocuments, fetchSyncStatus]);

  const analyzeDoc = async (doc, customQuestion) => {
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const res = await fetch(`${API}/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          doc_id: doc.doc_id,
          doc_type: 'casa',
          question: customQuestion || undefined
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
        toast.success('Analysis complete');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Analysis failed');
      }
    } catch (err) {
      toast.error('Analysis request failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const filtered = documents.filter(d =>
    d.title.toLowerCase().includes(search.toLowerCase()) ||
    d.reference.toLowerCase().includes(search.toLowerCase())
  );

  const undismissedUpdates = recentUpdates.filter(u => !u.dismissed);

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="casa-library-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              CASA Document Library
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              CAO, CAR, CAAP, MOS, AIP, ERSA &amp; ERSA GEN
            </p>
          </div>
          
          {/* Sync Status Panel */}
          <div className="flex items-center gap-2 sm:gap-3">
            {undismissedUpdates.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUpdates(true)}
                className="relative text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-amber-500 text-[10px] rounded-full flex items-center justify-center text-white">
                  {undismissedUpdates.length}
                </span>
              </Button>
            )}
            
            <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2">
              <div className="flex items-center gap-1.5">
                {syncStatus?.auto_sync_enabled ? (
                  <Cloud className="h-4 w-4 text-emerald-400" />
                ) : (
                  <CloudOff className="h-4 w-4 text-slate-500" />
                )}
                <span className="text-xs text-slate-400 hidden sm:inline">Auto-sync</span>
              </div>
              <Switch
                checked={syncStatus?.auto_sync_enabled ?? true}
                onCheckedChange={handleToggleAutoSync}
                className="data-[state=checked]:bg-emerald-600"
              />
            </div>
            
            <Button
              data-testid="sync-casa-btn"
              onClick={handleSync}
              disabled={syncing}
              size="sm"
              className="bg-[#003087] hover:bg-[#003087]/90 text-white"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Sync Now</span>
            </Button>
          </div>
        </div>

        {/* Last Sync Info */}
        {syncStatus?.last_sync && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            <span>Last synced: {new Date(syncStatus.last_sync).toLocaleString()}</span>
            {syncStatus.documents_synced > 0 && (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                +{syncStatus.documents_synced} new
              </Badge>
            )}
            {syncStatus.documents_updated > 0 && (
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">
                {syncStatus.documents_updated} updated
              </Badge>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="casa-search"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600 focus:ring-[#0ea5e9] focus:border-[#0ea5e9]"
          />
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800 h-auto flex-wrap gap-1 p-1">
            <TabsTrigger
              value="ALL"
              data-testid="tab-all"
              className="data-[state=active]:bg-[#003087] data-[state=active]:text-white text-slate-400 text-xs font-mono"
            >
              ALL
            </TabsTrigger>
            {CATEGORIES.map(cat => (
              <TabsTrigger
                key={cat}
              value={cat}
              data-testid={`tab-${cat.toLowerCase()}`}
              className="data-[state=active]:bg-[#003087] data-[state=active]:text-white text-slate-400 text-xs font-mono"
            >
              {cat.replace('_', ' ')}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-[160px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-12 w-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No documents found</p>
              <p className="text-xs text-slate-600 mt-1">Try loading sample data from the Dashboard</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((doc, idx) => (
                <Card
                  key={doc.doc_id}
                  data-testid={`casa-doc-${doc.doc_id}`}
                  className={`bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-all duration-200 cursor-pointer animate-fade-in stagger-${(idx % 6) + 1}`}
                  style={{ opacity: 0 }}
                  onClick={() => { setSelectedDoc(doc); setAnalysis(null); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Badge className="bg-slate-800 text-[#0ea5e9] border-slate-700 text-[10px] font-mono">
                        {doc.category.replace('_', ' ')}
                      </Badge>
                      <Badge className={`text-[10px] ${statusColors[doc.status] || statusColors.current}`}>
                        {doc.status === 'review_due' ? 'REVIEW DUE' : doc.status?.toUpperCase()}
                      </Badge>
                    </div>
                    <h3 className="text-sm font-medium text-slate-200 mb-2 line-clamp-2">{doc.title}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2">{doc.description}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50">
                      <span className="font-mono text-[10px] text-slate-500">Part {doc.part}</span>
                      <ChevronRight className="h-3 w-3 text-slate-600" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Document Detail Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-2xl bg-[#0f172a] border-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-slate-200">{selectedDoc?.title}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedDoc?.reference} &middot; Part {selectedDoc?.part}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 mt-4 pr-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-slate-800 text-[#0ea5e9] border-slate-700 text-xs font-mono">
                  {selectedDoc?.category}
                </Badge>
                <Badge className={`text-xs ${statusColors[selectedDoc?.status] || ''}`}>
                  {selectedDoc?.status?.toUpperCase()}
                </Badge>
                {selectedDoc?.url && (
                  <a
                    href={selectedDoc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#0ea5e9] hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> CASA Source
                  </a>
                )}
              </div>
              <div>
                <p className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-2">Description</p>
                <p className="text-sm text-slate-300">{selectedDoc?.description}</p>
              </div>
              <div>
                <p className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-2">Content Preview</p>
                <p className="text-sm text-slate-400">{selectedDoc?.content_preview}</p>
              </div>

              {/* AI Analysis */}
              <div className="border-t border-slate-800 pt-4">
                <p className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Sparkles className="h-3 w-3 text-[#0ea5e9]" /> AI Analysis
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    data-testid="ai-question-input"
                    placeholder="Ask a specific question..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className="bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600 text-sm"
                  />
                  <Button
                    data-testid="ai-analyze-btn"
                    onClick={() => analyzeDoc(selectedDoc, question)}
                    disabled={analyzing}
                    className="bg-[#003087] hover:bg-[#003087]/90 text-white whitespace-nowrap"
                  >
                    {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Analyze'}
                  </Button>
                </div>
                {analysis && (
                  <div data-testid="ai-analysis-result" className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
                    <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{analysis.response}</p>
                    <p className="text-[10px] text-slate-600 mt-3 font-mono">
                      Analyzed {new Date(analysis.analyzed_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Recent Updates Dialog */}
      <Dialog open={showUpdates} onOpenChange={setShowUpdates}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full bg-[#0f172a] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-200 flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-400" />
              Document Updates
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Recent changes to CASA documentation
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-3 pr-4">
              {undismissedUpdates.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No new updates</p>
              ) : (
                undismissedUpdates.map(update => (
                  <div
                    key={update.update_id}
                    className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 flex items-start gap-3"
                  >
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                      update.update_type === 'new_document' ? 'bg-emerald-500/10' : 'bg-blue-500/10'
                    }`}>
                      {update.update_type === 'new_document' ? (
                        <FileText className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <RefreshCw className="h-4 w-4 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">
                          {update.category}
                        </Badge>
                        <Badge className={`text-[9px] ${
                          update.update_type === 'new_document' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                            : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                        }`}>
                          {update.update_type === 'new_document' ? 'NEW' : 'UPDATED'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-200 font-medium truncate">{update.reference}</p>
                      <p className="text-xs text-slate-500 truncate">{update.title}</p>
                      <p className="text-[10px] text-slate-600 mt-1">
                        {new Date(update.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDismissUpdate(update.update_id)}
                      className="h-6 w-6 p-0 text-slate-500 hover:text-slate-300"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      </div>
    </PullToRefresh>
  );
}
